<template>
  <section v-if="isHomeRoute" class="fomo-hero shell-section">
    <transition name="intro-fade">
      <div v-if="showIntroOverlay" class="fomo-hero__intro" :class="{ 'is-done': introLeaving }" aria-hidden="true">
        <canvas ref="introCanvas" class="fomo-hero__intro-canvas"></canvas>
        <div
          class="fomo-hero__intro-subtitle"
          :class="{ 'is-visible': introSubtitleVisible }"
        >
          Fear of Missing Out
        </div>
      </div>
    </transition>

    <div :class="['fomo-hero__frame', 'shell-surface', { 'is-intro-active': showIntroOverlay }]">
      <div class="fomo-hero__media">
        <video autoplay muted playsinline loop src="../assets/vidFomo.mp4"></video>
      </div>

      <canvas ref="auroraCanvas" class="fomo-hero__aurora" aria-hidden="true"></canvas>
      <div class="fomo-hero__veil"></div>
      <div class="fomo-hero__spotlight"></div>
      <div class="fomo-hero__grid"></div>
      <div class="fomo-hero__rings" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div class="fomo-hero__scanlines"></div>
      <div class="fomo-hero__vignette"></div>

      <div class="fomo-hero__layout">
        <div class="fomo-hero__copy">
          <span class="fomo-hero__eyebrow">FOMO PLANES</span>

          <h1 class="fomo-hero__title">
            <span class="fomo-hero__title-line">Hay dos clases</span>
            <span class="fomo-hero__title-line fomo-hero__title-line--accent">de planes.</span>
          </h1>

          <p class="fomo-hero__statement">
            <span>Los que se venden en FOMO</span>
            <span>y los demás.</span>
          </p>

          <p class="fomo-hero__subtitle">{{ homeSubtitle }}</p>

          <div class="fomo-hero__actions">
            <button class="fomo-hero__action fomo-hero__action--primary" type="button" @click="scrollToEvents">
              <span class="fomo-hero__action-glow"></span>
              <span class="fomo-hero__action-ring"></span>
              <span class="fomo-hero__action-inner">
                <span class="fomo-hero__action-copy">{{ primaryActionLabel }}</span>
                <span class="fomo-hero__action-icon">
                  <i class="fas fa-arrow-right" aria-hidden="true"></i>
                </span>
              </span>
            </button>
            <button class="fomo-hero__action fomo-hero__action--secondary" type="button" @click="openWss">
              <span class="fomo-hero__action-glow"></span>
              <span class="fomo-hero__action-ring"></span>
              <span class="fomo-hero__action-inner">
                <span class="fomo-hero__action-copy">{{ secondaryActionLabel }}</span>
                <span class="fomo-hero__action-icon">
                  <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="fomo-hero__ticker" aria-label="FOMO highlights">
        <div class="fomo-hero__ticker-track">
          <span v-for="(item, index) in tickerItems" :key="`ticker-a-${index}`">{{ item }}</span>
          <span v-for="(item, index) in tickerItems" :key="`ticker-b-${index}`">{{ item }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const INTRO_SESSION_KEY = 'fomo-home-intro-played'

export default {
  name: 'SlideShow',

  props: {
    main: {
      required: false,
    },
    subtitle: {
      required: false,
    },
    type: {
      default: 2,
    },
    activeEventsCount: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
      prefersReducedMotion: false,
      auroraFrame: null,
      introFrame: null,
      auroraContext: null,
      introContext: null,
      introPhase: 'idle',
      introProgress: 0,
      introVisible: false,
      introStarted: false,
      introCompleted: false,
      introLeaving: false,
      introWordVisible: false,
      introSubtitleVisible: false,
      introStartedAt: 0,
      introFontSize: 0,
      introParticles: [],
      auroraResizeHandler: null,
      mediaQueryList: null,
      reduceMotionHandler: null,
      introFinishTimer: null,
      introSequenceTimers: [],
    }
  },

  computed: {
    isHomeRoute() {
      return this.$route && this.$route.name === 'home'
    },

    isMobileViewport() {
      return this.viewportWidth <= 900
    },

    homeSubtitle() {
      if (this.subtitle) {
        return this.subtitle
      }

      return this.isMobileViewport
        ? 'Entradas y listas activas para decidir rápido.'
        : 'Entradas, listas y experiencias curadas en un solo lugar para descubrir qué plan se mueve hoy.'
    },

    activeEventsLabel() {
      if (this.activeEventsCount && this.activeEventsCount > 0) {
        return this.activeEventsCount === 1 ? '1 evento activo' : `${this.activeEventsCount} eventos activos`
      }

      return 'Selección en vivo'
    },

    activeEventsShort() {
      if (this.activeEventsCount && this.activeEventsCount > 0) {
        return `${this.activeEventsCount}+ planes ahora`
      }

      return 'Agenda encendida'
    },

    activeEventsNarrative() {
      if (this.activeEventsCount && this.activeEventsCount > 0) {
        return `Hay ${this.activeEventsCount} oportunidades activas para entrar en la conversación antes de que suban.`
      }

      return 'La selección se actualiza en tiempo real para que el siguiente movimiento aparezca primero aquí.'
    },

    primaryActionLabel() {
      return this.isMobileViewport ? 'Ver agenda' : 'Explorar eventos'
    },

    secondaryActionLabel() {
      return this.isMobileViewport ? 'Crear evento' : 'Lanza tu evento'
    },

    showIntroOverlay() {
      return this.isHomeRoute && this.introVisible && !this.prefersReducedMotion
    },

    tickerItems() {
      return [
        this.activeEventsLabel,
        'Drops semanales',
        'Listas verificadas',
        'Accesos rapidos',
        'Eventos con pulso real',
        'Lanza tu noche en FOMO',
      ]
    },
  },

  watch: {
    isHomeRoute() {
      this.$nextTick(() => {
        this.handleRouteState()
      })
    },
  },

  mounted() {
    this.viewportWidth = window.innerWidth || 1280
    this.prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.auroraResizeHandler = this.handleResize.bind(this)
    window.addEventListener('resize', this.auroraResizeHandler)

    if (window.matchMedia) {
      this.mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reduceMotionHandler = (event) => {
        this.prefersReducedMotion = event.matches
        if (event.matches) {
          this.finishIntro(true)
        }
      }

      if (this.mediaQueryList.addEventListener) {
        this.mediaQueryList.addEventListener('change', this.reduceMotionHandler)
      } else if (this.mediaQueryList.addListener) {
        this.mediaQueryList.addListener(this.reduceMotionHandler)
      }
    }

    this.$nextTick(() => {
      this.handleRouteState()
    })
  },

  beforeUnmount() {
    if (this.auroraResizeHandler) {
      window.removeEventListener('resize', this.auroraResizeHandler)
    }

    if (this.mediaQueryList && this.reduceMotionHandler) {
      if (this.mediaQueryList.removeEventListener) {
        this.mediaQueryList.removeEventListener('change', this.reduceMotionHandler)
      } else if (this.mediaQueryList.removeListener) {
        this.mediaQueryList.removeListener(this.reduceMotionHandler)
      }
    }

    this.stopAuroraAnimation()
    this.stopIntroAnimation()

    if (this.introFinishTimer) {
      window.clearTimeout(this.introFinishTimer)
      this.introFinishTimer = null
    }
  },

  methods: {
    handleRouteState() {
      this.viewportWidth = window.innerWidth || 1280

      if (!this.isHomeRoute) {
        this.introVisible = false
        this.introLeaving = false
        this.introWordVisible = false
        this.introSubtitleVisible = false

        if (this.introFinishTimer) {
          window.clearTimeout(this.introFinishTimer)
          this.introFinishTimer = null
        }

        this.stopAuroraAnimation()
        this.stopIntroAnimation()
        return
      }

      this.startAuroraAnimation()
      this.maybePlayIntro()
    },

    handleResize() {
      this.viewportWidth = window.innerWidth || 1280
      this.sizeCanvas(this.$refs.auroraCanvas, 'auroraContext')

      if (this.showIntroOverlay && this.introStarted) {
        this.startIntroAnimation()
      }
    },

    maybePlayIntro() {
      if (this.prefersReducedMotion) {
        this.finishIntro(true)
        return
      }

      let hasPlayed = false

      try {
        hasPlayed = window.sessionStorage.getItem(INTRO_SESSION_KEY) === '1'
      } catch (error) {
        hasPlayed = false
      }

      if (hasPlayed) {
        this.finishIntro(true)
        return
      }

      if (this.introCompleted || this.introStarted) {
        return
      }

      this.introVisible = true
      this.introStarted = true
      this.introLeaving = false
      this.introWordVisible = false
      this.introSubtitleVisible = false

      this.$nextTick(() => {
        const startIntro = async () => {
          await this.ensureIntroFontReady()
          this.startIntroAnimation()
        }

        startIntro()
      })
    },

    async ensureIntroFontReady() {
      if (!document.fonts || !document.fonts.load) {
        return
      }

      try {
        await Promise.all([
          document.fonts.load('900 240px "Bebas Neue"'),
          document.fonts.load('900 240px "Inter"'),
        ])
      } catch (error) {
        return
      }
    },

    sizeCanvas(canvas, contextKey) {
      if (!canvas) {
        return null
      }

      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      const width = canvas.clientWidth || canvas.offsetWidth || window.innerWidth || 1
      const height = canvas.clientHeight || canvas.offsetHeight || window.innerHeight || 1

      canvas.width = Math.max(1, Math.round(width * ratio))
      canvas.height = Math.max(1, Math.round(height * ratio))

      const context = canvas.getContext('2d')
      context.setTransform(ratio, 0, 0, ratio, 0, 0)
      this[contextKey] = context
      return context
    },

    startAuroraAnimation() {
      if (!this.isHomeRoute || this.auroraFrame) {
        return
      }

      const context = this.sizeCanvas(this.$refs.auroraCanvas, 'auroraContext')
      if (!context) {
        return
      }

      const render = (time) => {
        const canvas = this.$refs.auroraCanvas
        const ctx = this.auroraContext

        if (!canvas || !ctx || !this.isHomeRoute) {
          this.stopAuroraAnimation()
          return
        }

        const width = canvas.clientWidth || 1
        const height = canvas.clientHeight || 1
        const t = (time || 0) * (this.prefersReducedMotion ? 0.00008 : 0.00018)

        ctx.clearRect(0, 0, width, height)

        const ribbons = [
          { x: width * 0.24, y: height * 0.2, rx: width * 0.28, ry: height * 0.12, color: '110, 232, 255', alpha: 0.16, drift: 0.9 },
          { x: width * 0.74, y: height * 0.3, rx: width * 0.24, ry: height * 0.14, color: '164, 150, 255', alpha: 0.14, drift: 1.2 },
          { x: width * 0.62, y: height * 0.7, rx: width * 0.32, ry: height * 0.16, color: '240, 245, 255', alpha: 0.08, drift: 0.7 },
        ]

        ribbons.forEach((ribbon, index) => {
          const offsetX = Math.sin((t * ribbon.drift) + index) * width * 0.04
          const offsetY = Math.cos((t * (ribbon.drift + 0.3)) + index) * height * 0.05
          const gradient = ctx.createRadialGradient(
            ribbon.x + offsetX,
            ribbon.y + offsetY,
            0,
            ribbon.x + offsetX,
            ribbon.y + offsetY,
            Math.max(ribbon.rx, ribbon.ry),
          )
          gradient.addColorStop(0, `rgba(${ribbon.color}, ${ribbon.alpha})`)
          gradient.addColorStop(0.42, `rgba(${ribbon.color}, ${ribbon.alpha * 0.55})`)
          gradient.addColorStop(1, `rgba(${ribbon.color}, 0)`)
          ctx.fillStyle = gradient
          ctx.beginPath()
          ctx.ellipse(ribbon.x + offsetX, ribbon.y + offsetY, ribbon.rx, ribbon.ry, Math.sin(t + index) * 0.24, 0, Math.PI * 2)
          ctx.fill()
        })

        this.auroraFrame = window.requestAnimationFrame(render)
      }

      this.auroraFrame = window.requestAnimationFrame(render)
    },

    stopAuroraAnimation() {
      if (this.auroraFrame) {
        window.cancelAnimationFrame(this.auroraFrame)
        this.auroraFrame = null
      }
    },

    startIntroAnimation() {
      const canvas = this.$refs.introCanvas
      if (!canvas) {
        this.finishIntro(true)
        return
      }

      const context = canvas.getContext('2d')
      if (!context) {
        this.finishIntro(true)
        return
      }

      const width = canvas.width = window.innerWidth
      const height = canvas.height = window.innerHeight

      const sampleText = (text, fontSize) => {
        const offscreen = document.createElement('canvas')
        offscreen.width = width
        offscreen.height = height
        const offscreenContext = offscreen.getContext('2d')

        if (!offscreenContext) {
          return []
        }

        offscreenContext.fillStyle = '#ffffff'
        offscreenContext.font = `900 ${fontSize}px "Bebas Neue", sans-serif`
        offscreenContext.textAlign = 'center'
        offscreenContext.textBaseline = 'middle'
        offscreenContext.fillText(text, width / 2, height / 2)

        const imageData = offscreenContext.getImageData(0, 0, width, height).data
        const points = []
        const step = Math.max(3, Math.floor(width / 120))

        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            const index = ((y * width) + x) * 4
            if (imageData[index + 3] > 128) {
              points.push({ x, y })
            }
          }
        }

        return points
      }

      const fontSize = Math.min(width * 0.28, height * 0.35, 280)
      const targets = sampleText('FOMO', fontSize)

      if (!targets.length) {
        this.finishIntro(true)
        return
      }

      const colors = ['#7C5CFC', '#9D7FFF', '#C4B5FD', '#E2DEFF', '#A8A0C8', '#D4D0E8', '#6040D0', '#B8B0D8']
      const particleCount = Math.min(targets.length, 600)
      const stride = Math.max(1, Math.floor(targets.length / particleCount))
      const chosenTargets = []

      for (let index = 0; index < particleCount; index += 1) {
        chosenTargets.push(targets[index * stride] || targets[targets.length - 1])
      }

      const particles = chosenTargets.map((target, index) => {
        const edge = index % 4
        let startX = 0
        let startY = 0

        if (edge === 0) {
          startX = -80 - (Math.random() * 120)
          startY = Math.random() * height
        } else if (edge === 1) {
          startX = width + 80 + (Math.random() * 120)
          startY = Math.random() * height
        } else if (edge === 2) {
          startX = Math.random() * width
          startY = -80 - (Math.random() * 120)
        } else {
          startX = Math.random() * width
          startY = height + 80 + (Math.random() * 120)
        }

        const dx = target.x - (width / 2)
        const dy = target.y - (height / 2)
        const distance = Math.sqrt((dx * dx) + (dy * dy)) || 1
        const speed = 250 + (Math.random() * 350)

        return {
          sx: startX,
          sy: startY,
          tx: target.x,
          ty: target.y,
          x: startX,
          y: startY,
          r: (Math.random() * 1.6) + 0.7,
          color: colors[Math.floor(Math.random() * colors.length)],
          ex: (dx / distance) * speed,
          ey: ((dy / distance) * speed) + ((Math.random() - 0.5) * 180),
          alpha: 1,
        }
      })

      const gatherDuration = 1600
      const holdDuration = 600
      const explodeDuration = 700
      const totalDuration = gatherDuration + holdDuration + explodeDuration

      this.introStartedAt = 0
      this.introPhase = 'assemble'
      this.introProgress = 0
      this.introSubtitleVisible = false
      this.stopIntroAnimation()

      const render = (timestamp) => {
        if (!this.showIntroOverlay) {
          this.stopIntroAnimation()
          return
        }

        if (!this.introStartedAt) {
          this.introStartedAt = timestamp
        }

        const elapsed = timestamp - this.introStartedAt
        context.clearRect(0, 0, width, height)

        const holdStart = gatherDuration
        const holdEnd = gatherDuration + holdDuration
        const holdProgress = elapsed >= holdStart
          ? Math.min((elapsed - holdStart) / holdDuration, 1)
          : 0

        const ambientGlow = context.createRadialGradient(width * 0.5, height * 0.5, 0, width * 0.5, height * 0.5, Math.max(width, height) * 0.32)
        ambientGlow.addColorStop(0, 'rgba(124, 92, 252, 0.08)')
        ambientGlow.addColorStop(0.45, 'rgba(157, 127, 255, 0.03)')
        ambientGlow.addColorStop(1, 'rgba(2, 1, 10, 0)')
        context.fillStyle = ambientGlow
        context.fillRect(0, 0, width, height)

        particles.forEach((particle) => {
          if (elapsed < gatherDuration) {
            const progress = this.easeOutQuint(Math.min(elapsed / gatherDuration, 1))
            particle.x = particle.sx + ((particle.tx - particle.sx) * progress)
            particle.y = particle.sy + ((particle.ty - particle.sy) * progress)
            particle.alpha = 0.4 + (progress * 0.6)
          } else if (elapsed < gatherDuration + holdDuration) {
            particle.x = particle.tx
            particle.y = particle.ty
            particle.alpha = 1
          } else {
            const progress = this.easeInCubic(Math.min((elapsed - gatherDuration - holdDuration) / explodeDuration, 1))
            particle.x = particle.tx + (particle.ex * progress)
            particle.y = particle.ty + (particle.ey * progress)
            particle.alpha = 1 - progress
          }

          context.beginPath()
          context.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2)
          context.fillStyle = particle.color
          context.globalAlpha = Math.max(0, particle.alpha)
          context.fill()
        })

        context.globalAlpha = 1

        if (elapsed >= holdStart && elapsed < holdEnd) {
          const revealProgress = Math.min(Math.max((holdProgress - 0.18) / 0.58, 0), 1)

          if (revealProgress > 0) {
            const textOpacity = 0.12 + (revealProgress * 0.2)

            context.save()
            context.textAlign = 'center'
            context.textBaseline = 'middle'
            context.font = `900 ${fontSize}px "Bebas Neue", sans-serif`
            context.fillStyle = `rgba(237, 232, 255, ${textOpacity})`
            context.shadowBlur = 26 + (revealProgress * 36)
            context.shadowColor = `rgba(124, 92, 252, ${0.14 + (revealProgress * 0.2)})`
            context.fillText('FOMO', width / 2, height / 2)
            context.restore()

            const pulse = (0.08 + (Math.sin(revealProgress * Math.PI) * 0.12)) * revealProgress
            const pulseGlow = context.createRadialGradient(width * 0.5, height * 0.5, 0, width * 0.5, height * 0.5, fontSize * 1.1)
            pulseGlow.addColorStop(0, `rgba(196, 181, 253, ${pulse})`)
            pulseGlow.addColorStop(0.5, `rgba(124, 92, 252, ${pulse * 0.34})`)
            pulseGlow.addColorStop(1, 'rgba(2, 1, 10, 0)')
            context.fillStyle = pulseGlow
            context.fillRect(0, 0, width, height)

            const shimmerProgress = Math.min(Math.max((holdProgress - 0.44) / 0.42, 0), 1)
            if (shimmerProgress > 0) {
              const shimmerX = (width * 0.5) - fontSize + ((fontSize * 2) * shimmerProgress)
              const shimmer = context.createLinearGradient(shimmerX - 100, 0, shimmerX + 100, 0)
              shimmer.addColorStop(0, 'rgba(255, 255, 255, 0)')
              shimmer.addColorStop(0.48, 'rgba(255, 255, 255, 0.04)')
              shimmer.addColorStop(0.5, 'rgba(255, 255, 255, 0.34)')
              shimmer.addColorStop(0.52, 'rgba(196, 181, 253, 0.28)')
              shimmer.addColorStop(1, 'rgba(255, 255, 255, 0)')
              context.fillStyle = shimmer
              context.fillRect(shimmerX - 100, height * 0.22, 200, height * 0.56)
            }
          }
        }

        if (elapsed > gatherDuration + (holdDuration * 0.3)) {
          this.introSubtitleVisible = true
        }

        if (elapsed < totalDuration) {
          this.introFrame = window.requestAnimationFrame(render)
          return
        }

        this.finishIntro()
      }

      this.introFrame = window.requestAnimationFrame(render)
    },

    stopIntroAnimation() {
      if (this.introFrame) {
        window.cancelAnimationFrame(this.introFrame)
        this.introFrame = null
      }
    },

    seedIntroParticles() {},

    finishIntro(skipStorage) {
      if (!skipStorage && !this.introLeaving) {
        this.stopIntroAnimation()
        this.introCompleted = true
        this.introLeaving = true

        try {
          window.sessionStorage.setItem(INTRO_SESSION_KEY, '1')
        } catch (error) {
          return null
        }

        if (this.introFinishTimer) {
          window.clearTimeout(this.introFinishTimer)
        }

        this.introFinishTimer = window.setTimeout(() => {
          this.introVisible = false
          this.introLeaving = false
          this.introWordVisible = false
          this.introSubtitleVisible = false
          this.introFinishTimer = null
        }, 900)

        return null
      }

      this.stopIntroAnimation()
      this.introVisible = false
      this.introCompleted = true
      this.introLeaving = false
      this.introWordVisible = false
      this.introSubtitleVisible = false

      if (!skipStorage) {
        try {
          window.sessionStorage.setItem(INTRO_SESSION_KEY, '1')
        } catch (error) {
          return
        }
      }
    },

    lerp(start, end, amount) {
      return start + ((end - start) * amount)
    },

    easeOutCubic(value) {
      return 1 - Math.pow(1 - value, 3)
    },

    easeOutQuint(value) {
      return 1 - Math.pow(1 - value, 5)
    },

    easeInCubic(value) {
      return value * value * value
    },

    easeInOutCubic(value) {
      return value < 0.5
        ? 4 * value * value * value
        : 1 - Math.pow(-2 * value + 2, 3) / 2
    },

    scrollToEvents() {
      const section = document.getElementById('agenda-activa')

      if (section) {
        section.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
      }
    },

    openWss() {
      this.$router.push({ name: 'launch-event' })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');

.fomo-hero {
  padding-top: 6px;
  padding-bottom: 10px;
}

.fomo-hero__frame {
  --hero-silver: rgba(235, 239, 247, 0.92);
  --hero-muted: rgba(193, 201, 214, 0.82);
  position: relative;
  overflow: hidden;
  min-height: clamp(560px, 72vw, 860px);
  padding: clamp(22px, 3vw, 34px);
  background: linear-gradient(180deg, rgba(9, 10, 16, 0.96), rgba(4, 5, 10, 0.96));
}

.fomo-hero__frame.is-intro-active .fomo-hero__layout,
.fomo-hero__frame.is-intro-active .fomo-hero__ticker {
  opacity: 0;
}

.fomo-hero__media,
.fomo-hero__media video,
.fomo-hero__aurora,
.fomo-hero__veil,
.fomo-hero__spotlight,
.fomo-hero__grid,
.fomo-hero__scanlines,
.fomo-hero__vignette {
  position: absolute;
  inset: 0;
}

.fomo-hero__media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.05);
  filter: saturate(0.66) brightness(0.28) contrast(1.08);
}

.fomo-hero__aurora {
  width: 100%;
  height: 100%;
  opacity: 0.96;
}

.fomo-hero__veil {
  background:
    linear-gradient(180deg, rgba(5, 6, 10, 0.82), rgba(5, 6, 10, 0.48) 34%, rgba(5, 6, 10, 0.78) 100%),
    radial-gradient(circle at 50% 18%, rgba(255, 255, 255, 0.08), transparent 18%),
    radial-gradient(circle at 62% 40%, rgba(134, 124, 255, 0.12), transparent 24%),
    radial-gradient(circle at 36% 68%, rgba(110, 230, 255, 0.08), transparent 26%);
}

.fomo-hero__spotlight {
  background:
    radial-gradient(circle at 50% 34%, rgba(236, 241, 248, 0.14), transparent 20%),
    radial-gradient(circle at 50% 40%, rgba(110, 230, 255, 0.08), transparent 32%);
  filter: blur(32px);
}

.fomo-hero__grid {
  opacity: 0.18;
  background-image:
    linear-gradient(rgba(228, 233, 241, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(228, 233, 241, 0.08) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.66), transparent 100%);
}

.fomo-hero__rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.fomo-hero__rings span {
  position: absolute;
  left: 50%;
  top: 42%;
  border-radius: 50%;
  border: 1px solid rgba(226, 232, 241, 0.12);
  animation: hero-ring 18s linear infinite;
}

.fomo-hero__rings span:nth-child(1) {
  width: clamp(220px, 32vw, 410px);
  height: clamp(220px, 32vw, 410px);
}

.fomo-hero__rings span:nth-child(2) {
  width: clamp(150px, 24vw, 300px);
  height: clamp(150px, 24vw, 300px);
  border-color: rgba(132, 224, 247, 0.14);
  animation-duration: 12s;
  animation-direction: reverse;
}

.fomo-hero__rings span:nth-child(3) {
  width: clamp(84px, 14vw, 180px);
  height: clamp(84px, 14vw, 180px);
  border-color: rgba(155, 144, 255, 0.14);
  animation-duration: 9s;
}

.fomo-hero__scanlines {
  opacity: 0.14;
  background: repeating-linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0 1px, transparent 1px 4px);
  mix-blend-mode: soft-light;
}

.fomo-hero__vignette {
  background: radial-gradient(circle at center, transparent 48%, rgba(2, 3, 8, 0.74) 100%);
}

.fomo-hero__layout {
  position: relative;
  z-index: 2;
  min-height: calc(clamp(560px, 72vw, 860px) - (2 * clamp(22px, 3vw, 34px)));
  display: grid;
  place-items: center;
  text-align: center;
}

.fomo-hero__copy {
  max-width: 860px;
  display: grid;
  justify-items: center;
  gap: 18px;
}

.fomo-hero__eyebrow,
.fomo-hero__pill,
.fomo-hero__card-kicker,
.fomo-hero__ticker-track span {
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.fomo-hero__eyebrow {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  color: rgba(244, 247, 251, 0.92);
  letter-spacing: 0.18em;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(14, 16, 24, 0.72));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 18px 34px rgba(0, 0, 0, 0.18);
}

.fomo-hero__metrics {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.fomo-hero__pill {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  color: rgba(235, 239, 247, 0.88);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(12, 14, 22, 0.72));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), inset 0 -16px 24px rgba(113, 124, 156, 0.06);
}

.fomo-hero__title {
  margin: 0;
  max-width: none;
  color: transparent;
  background: linear-gradient(180deg, #ffffff 0%, #eef3fa 36%, #cad4e2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(4.4rem, 10vw, 8.2rem);
  font-weight: 600;
  line-height: 0.84;
  letter-spacing: -0.05em;
  text-shadow: 0 18px 42px rgba(5, 8, 18, 0.34);
}

.fomo-hero__title-line,
.fomo-hero__statement span {
  display: block;
}

.fomo-hero__title-line {
  white-space: nowrap;
}

.fomo-hero__title-line--accent {
  background: linear-gradient(96deg, #ffffff 0%, #eef3fa 24%, #d7def0 44%, #b8b0e6 72%, #95dce2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 24px rgba(149, 220, 226, 0.14));
}

.fomo-hero__statement {
  margin: 0;
  max-width: 20ch;
  color: var(--hero-silver);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(1.16rem, 2.4vw, 1.78rem);
  font-weight: 760;
  line-height: 1.04;
  letter-spacing: -0.03em;
}

.fomo-hero__subtitle {
  max-width: 50ch;
  margin: 0;
  color: var(--hero-muted);
  font-family: 'Manrope', sans-serif;
  font-size: 1rem;
  line-height: 1.7;
}

.fomo-hero__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: min(100%, 560px);
  gap: 14px;
  padding-top: 10px;
}

.fomo-hero__action {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  appearance: none;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 56px;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  color: rgba(244, 247, 251, 0.95);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  background: rgba(10, 10, 14, 0.16);
  backdrop-filter: blur(34px) saturate(135%);
  -webkit-backdrop-filter: blur(34px) saturate(135%);
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    filter 180ms ease;
}

.fomo-hero__action::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.08) 16%, rgba(118, 208, 213, 0.04) 54%, rgba(255, 255, 255, 0.1) 100%);
  opacity: 0.32;
  pointer-events: none;
}

.fomo-hero__action::after {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  background:
    radial-gradient(circle at 24% -12%, rgba(255, 255, 255, 0.28), transparent 34%),
    radial-gradient(circle at 84% 88%, rgba(140, 129, 189, 0.12), transparent 26%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03) 22%, rgba(12, 12, 18, 0.03) 54%, rgba(118, 208, 213, 0.04) 100%);
  opacity: 0.72;
  pointer-events: none;
}

.fomo-hero__action-glow {
  position: absolute;
  inset: -28%;
  border-radius: inherit;
  background:
    radial-gradient(circle at 18% 10%, rgba(255, 255, 255, 0.14), transparent 30%),
    radial-gradient(circle at 68% 86%, rgba(140, 129, 189, 0.16), transparent 42%);
  filter: blur(24px);
  opacity: 0.34;
  pointer-events: none;
}

.fomo-hero__action-ring {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.26), rgba(255, 255, 255, 0.04) 26%, rgba(140, 129, 189, 0.04) 60%, rgba(255, 255, 255, 0.12) 84%, rgba(118, 208, 213, 0.06) 100%);
  opacity: 0.68;
  pointer-events: none;
}

.fomo-hero__action-inner {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: calc(100% - 2px);
  min-height: 54px;
  margin: 1px;
  padding: 0 10px 0 18px;
  border-radius: inherit;
  background:
    radial-gradient(circle at 24% 0%, rgba(255, 255, 255, 0.12), transparent 34%),
    linear-gradient(180deg, rgba(28, 31, 42, 0.34), rgba(11, 11, 17, 0.74));
  color: #eef1f7;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    inset 0 -16px 24px rgba(118, 208, 213, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.03);
}

.fomo-hero__action-copy {
  white-space: nowrap;
  text-align: left;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.08);
}

.fomo-hero__action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #f4f7fb;
  background:
    radial-gradient(circle at 30% 24%, rgba(255, 255, 255, 0.22), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(118, 208, 213, 0.06));
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -8px 14px rgba(140, 129, 189, 0.08),
    0 8px 18px rgba(4, 6, 12, 0.14);
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.fomo-hero__action-icon i {
  font-size: 0.9rem;
}

.fomo-hero__action--primary {
  box-shadow:
    0 18px 34px rgba(2, 4, 10, 0.3),
    0 10px 18px rgba(118, 208, 213, 0.1);
}

.fomo-hero__action--primary .fomo-hero__action-glow {
  inset: -10px;
  background:
    radial-gradient(circle at 16% 10%, rgba(228, 232, 239, 0.16), transparent 28%),
    radial-gradient(circle at 82% 84%, rgba(118, 208, 213, 0.14), transparent 34%);
  filter: blur(14px);
  opacity: 0.22;
}

.fomo-hero__action--primary .fomo-hero__action-ring {
  inset: 1px;
  background:
    radial-gradient(circle at 18% 12%, rgba(255, 255, 255, 0.22), transparent 26%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.04) 26%, rgba(140, 129, 189, 0.08) 60%, rgba(255, 255, 255, 0.12) 100%);
  opacity: 0.78;
}

.fomo-hero__action--primary::after {
  inset: 1px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 20%),
    radial-gradient(circle at 12% 0%, rgba(255, 255, 255, 0.08), transparent 22%);
  opacity: 0.22;
}

.fomo-hero__action--primary .fomo-hero__action-inner {
  width: calc(100% - 4px);
  min-height: 52px;
  margin: 2px;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.1), transparent 34%),
    linear-gradient(180deg, rgba(24, 28, 40, 0.44), rgba(11, 11, 17, 0.82));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -16px 24px rgba(118, 208, 213, 0.06),
    0 0 0 1px rgba(255, 255, 255, 0.04);
}

.fomo-hero__action--primary .fomo-hero__action-icon {
  background:
    radial-gradient(circle at 30% 24%, rgba(255, 255, 255, 0.24), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(118, 208, 213, 0.12));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.26),
    inset 0 -8px 14px rgba(118, 208, 213, 0.12),
    0 8px 18px rgba(4, 6, 12, 0.16);
}

.fomo-hero__action--secondary {
  box-shadow:
    0 16px 30px rgba(2, 4, 10, 0.24),
    0 10px 18px rgba(140, 129, 189, 0.08);
}

.fomo-hero__action--secondary .fomo-hero__action-glow {
  opacity: 0.24;
}

.fomo-hero__action--secondary .fomo-hero__action-ring {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.06) 20%, rgba(140, 129, 189, 0.06) 52%, rgba(255, 255, 255, 0.14) 80%, rgba(118, 208, 213, 0.08) 100%);
}

.fomo-hero__action--secondary .fomo-hero__action-inner {
  background:
    radial-gradient(circle at 24% 0%, rgba(255, 255, 255, 0.09), transparent 34%),
    linear-gradient(180deg, rgba(22, 25, 35, 0.24), rgba(10, 10, 16, 0.56));
}

.fomo-hero__action:hover {
  transform: none;
  filter: brightness(1.04);
}

.fomo-hero__action:hover .fomo-hero__action-glow {
  opacity: 0.72;
}

.fomo-hero__action:hover .fomo-hero__action-icon {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    inset 0 -8px 14px rgba(118, 208, 213, 0.14),
    0 10px 20px rgba(4, 6, 12, 0.18);
}

.fomo-hero__support {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding-top: 2px;
}

.fomo-hero__support-item {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  color: rgba(214, 221, 232, 0.78);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(12, 13, 20, 0.56));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 12px 24px rgba(0, 0, 0, 0.16);
}

.fomo-hero__ticker {
  position: relative;
  z-index: 2;
  overflow: hidden;
  margin-top: 8px;
  padding: 14px 0 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.fomo-hero__ticker-track {
  display: flex;
  align-items: center;
  gap: 18px;
  width: max-content;
  animation: hero-marquee 24s linear infinite;
}

.fomo-hero__ticker-track span {
  display: inline-flex;
  align-items: center;
  gap: 18px;
  color: rgba(214, 221, 232, 0.76);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.fomo-hero__ticker-track span::after {
  content: '';
  width: 28px;
  height: 1px;
  background: linear-gradient(90deg, rgba(110, 230, 255, 0.1), rgba(255, 255, 255, 0.28), rgba(155, 144, 255, 0.1));
}

.fomo-hero__intro {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #02010A;
  overflow: hidden;
  pointer-events: none;
}

.fomo-hero__intro.is-done {
  animation: intro-overlay-wipe 900ms cubic-bezier(0.76, 0, 0.24, 1) forwards;
}

.fomo-hero__intro-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.fomo-hero__intro-wordmark {
  position: relative;
  z-index: 1;
  color: #EDE8FF;
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(7rem, 24vw, 18rem);
  line-height: 0.86;
  letter-spacing: 0.06em;
  text-shadow:
    0 0 36px rgba(124, 92, 252, 0.18),
    0 0 72px rgba(124, 92, 252, 0.12);
  opacity: 0;
  transform: scale(0.9) translateY(14px);
  filter: blur(18px);
  transition:
    opacity 900ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 900ms cubic-bezier(0.16, 1, 0.3, 1),
    filter 900ms cubic-bezier(0.16, 1, 0.3, 1);
}

.fomo-hero__intro-wordmark.is-visible {
  opacity: 1;
  transform: scale(1) translateY(0);
  filter: blur(0);
}

.fomo-hero__intro-subtitle {
  position: absolute;
  bottom: 28%;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(124, 92, 252, 0);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(0.7rem, 1.5vw, 0.85rem);
  font-weight: 700;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.8s ease;
}

.fomo-hero__intro-subtitle.is-visible {
  color: rgba(124, 92, 252, 0.6);
}

.intro-fade-leave-active {
  transition: opacity 280ms ease;
}

.intro-fade-leave-to {
  opacity: 0;
}

@keyframes hero-ring {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes hero-marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

@keyframes intro-overlay-wipe {
  0% {
    clip-path: inset(0 0 0 0);
  }

  100% {
    clip-path: inset(0 0 100% 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fomo-hero__rings span,
  .fomo-hero__ticker-track {
    animation: none;
  }

  .fomo-hero__action {
    transition: none;
  }

  .fomo-hero__intro-subtitle {
    transition: none;
  }
}

@media (max-width: 1100px) {
  .fomo-hero__frame {
    min-height: clamp(600px, 82vw, 860px);
  }

  .fomo-hero__title {
    max-width: none;
  }

  .fomo-hero__actions {
    width: min(100%, 580px);
  }
}

@media (max-width: 720px) {
  .fomo-hero__frame {
    min-height: 0;
    padding: 16px 16px 8px;
  }

  .fomo-hero__layout {
    min-height: 0;
    place-items: start center;
    align-content: start;
    padding-top: 10px;
    padding-bottom: 4px;
  }

  .fomo-hero__title {
    max-width: none;
    font-size: clamp(2.65rem, 10.4vw, 3.8rem);
  }

  .fomo-hero__statement {
    font-size: 1.06rem;
    line-height: 1.08;
  }

  .fomo-hero__subtitle {
    max-width: 30ch;
    font-size: 0.9rem;
    line-height: 1.56;
  }

  .fomo-hero__copy {
    gap: 12px;
  }

  .fomo-hero__actions {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding-top: 4px;
  }

  .fomo-hero__action {
    min-height: 52px;
  }

  .fomo-hero__action-inner {
    gap: 10px;
    min-height: 50px;
    padding: 0 8px 0 12px;
  }

  .fomo-hero__action-copy {
    white-space: normal;
    font-size: 0.72rem;
    line-height: 1.05;
  }

  .fomo-hero__action-icon {
    flex-basis: 32px;
    width: 32px;
    height: 32px;
  }

  .fomo-hero__rings span {
    top: 31%;
  }

  .fomo-hero__ticker {
    margin-top: 0;
    padding: 10px 0 2px;
  }

  .fomo-hero__ticker-track {
    animation-duration: 18s;
  }

  .fomo-hero__intro-subtitle {
    bottom: 22%;
    max-width: calc(100vw - 36px);
    text-align: center;
    white-space: normal;
    letter-spacing: 0.16em;
  }
}
</style>
